import React, { useCallback, useEffect, useState } from 'react';
import { agentApi, resolveAgentApiError } from '../services/agentApi';
import { branchApi, resolveBranchApiError } from '../services/branchApi';
import FdModal from '../components/FdModal';
import useScopePermissions from '../hooks/useScopePermissions';
import { useAuth } from '../context/AuthContext';
import { todayIso, triggerCsvDownload, triggerExcelLikeDownload } from '../utils/exportUtils';
import TablePagination, { usePagination } from '../components/TablePagination';

const AGENT_POSITIONS = [
  'SOA',
  'Sales Advisor',
  'Unit Manager',
  'Branch Manager',
  'Assistant Area Head',
  'Area Head',
  'Senior Area Head',
  'Division Head',
  'CEO',
];


export default function AgentsPage() {
  const { hasScope } = useScopePermissions();
  const { isAdmin, userBranchId, userBranchName } = useAuth();
  const canCreate = hasScope('agents.create');
  const canUpdate = hasScope('agents.update');
  const canDelete = hasScope('agents.delete');

  const [agents, setAgents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    position: 'all',
    branch: 'all',
  });

  const [formData, setFormData] = useState({
    full_name: '',
    position: 'Sales Advisor',
    branch_id: '',
    email: '',
    phone: '',
    tin_number: '',
    status: 'Active',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadAgents = useCallback(async (options = {}) => {
    const { clearFeedback = true } = options;

    setIsLoading(true);
    setError('');
    if (clearFeedback) {
      setMessage('');
    }

    try {
      const response = await agentApi.listAgents({ bustCache: true });
      const agentList = response?.data?.agents || response?.agents || [];
      setAgents(Array.isArray(agentList) ? agentList : []);
      if (clearFeedback && agentList.length === 0) {
        setMessage('No agents found.');
      }
    } catch (err) {
      setAgents([]);
      setError(resolveAgentApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadBranches = useCallback(async () => {
    setIsLoadingBranches(true);

    try {
      const response = await branchApi.listBranches({ bustCache: true });
      const branchList = response?.data?.branches || response?.branches || [];
      setBranches(Array.isArray(branchList) ? branchList : []);
    } catch (err) {
      setBranches([]);
      console.error('Failed to load branches:', resolveBranchApiError(err));
    } finally {
      setIsLoadingBranches(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
    loadBranches();
  }, [loadAgents, loadBranches]);

  const filteredAgents = agents.filter((agent) => {
    const matchesSearch = agent.full_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
                          agent.email?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status === 'all' || agent.status === filters.status;
    const matchesPosition = filters.position === 'all' || agent.position === filters.position;
    const matchesBranch = filters.branch === 'all' || Number(agent.branch_id) === Number(filters.branch);
    return matchesSearch && matchesStatus && matchesPosition && matchesBranch;
  });

  const pagination = usePagination(filteredAgents);

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  const EXPORT_HEADERS = ['Name', 'Position', 'Branch', 'Email', 'Phone', 'TIN Number', 'Status', 'Created At'];

  function buildExportRows() {
    return filteredAgents.map((agent) => [
      agent.full_name || '',
      agent.position || '',
      agent.branch_name || 'Unassigned',
      agent.email || '',
      agent.phone || '',
      agent.tin_number || '',
      agent.status || '',
      agent.created_at || '',
    ]);
  }

  function exportCsv() {
    triggerCsvDownload(`agents-${todayIso()}.csv`, EXPORT_HEADERS, buildExportRows());
  }

  function exportExcel() {
    triggerExcelLikeDownload(`agents-${todayIso()}.xls`, EXPORT_HEADERS, buildExportRows());
  }

  function handleFormChange(event) {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function openAddForm() {
    if (!canCreate) {
      setError('You do not have permission to add agents.');
      return;
    }

    setIsEditMode(false);
    setSelectedAgent(null);
    setFormData({
      full_name: '',
      position: 'Sales Advisor',
      branch_id: !isAdmin && userBranchId != null ? String(userBranchId) : '',
      email: '',
      phone: '',
      tin_number: '',
      status: 'Active',
    });
    setError('');
    setIsFormOpen(true);
  }

  function openEditForm(agent) {
    if (!canUpdate) {
      setError('You do not have permission to edit agents.');
      return;
    }

    setIsEditMode(true);
    setSelectedAgent(agent);
    setFormData({
      full_name: agent.full_name || '',
      position: agent.position || 'Sales Advisor',
      branch_id: agent.branch_id || '',
      email: agent.email || '',
      phone: agent.phone || '',
      tin_number: agent.tin_number || '',
      status: agent.status || 'Active',
    });
    setError('');
    setIsFormOpen(true);
  }

  function closeForm() {
    setIsFormOpen(false);
    setSelectedAgent(null);
    setFormData({ full_name: '', position: 'Sales Advisor', branch_id: '', email: '', phone: '', tin_number: '', status: 'Active' });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setMessage('');

    if (isEditMode && !canUpdate) {
      setError('You do not have permission to edit agents.');
      return;
    }

    if (!isEditMode && !canCreate) {
      setError('You do not have permission to add agents.');
      return;
    }

    if (!formData.full_name.trim()) {
      setError('Agent name is required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        branch_id: formData.branch_id ? parseInt(formData.branch_id) : null,
      };

      if (isEditMode) {
        await agentApi.updateAgent(selectedAgent.id, payload);
        setMessage('Agent updated successfully.');
      } else {
        await agentApi.createAgent(payload);
        setMessage('Agent created successfully.');
      }
      closeForm();
      await loadAgents({ clearFeedback: false });
    } catch (err) {
      setError(resolveAgentApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(agent) {
    if (!canDelete) {
      setError('You do not have permission to delete agents.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${agent.full_name}"?`)) return;

    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      await agentApi.deleteAgent(agent.id);
      setMessage('Agent deleted successfully.');
      await loadAgents({ clearFeedback: false });
    } catch (err) {
      setError(resolveAgentApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <h1>Agent Management</h1>
        <p>Manage your sales agents and their assignments.</p>
      </section>

      <section className="dashboard-content">
        <div className="dashboard-toolbar">
          <div className="dashboard-filters">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search agent name or email..."
            />
            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            <select name="position" value={filters.position} onChange={handleFilterChange}>
              <option value="all">All Positions</option>
              {AGENT_POSITIONS.map((pos) => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
            {isAdmin ? (
              <select name="branch" value={filters.branch} onChange={handleFilterChange}>
                <option value="all">All Branches</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            ) : (
              <span className="dashboard-branch-pill">
                Branch: {userBranchName || 'Unassigned'}
              </span>
            )}
          </div>
          <div className="dashboard-actions">
            <button type="button" className="profile-secondary-btn" onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="profile-secondary-btn" onClick={exportExcel}>
              Export Excel
            </button>
            {canCreate ? (
              <button type="button" className="profile-secondary-btn" onClick={openAddForm}>
                + Add Agent
              </button>
            ) : null}
          </div>
        </div>

        {error && !isFormOpen && <div className="error-box">{error}</div>}
        {message && <div className="success-box">{message}</div>}

        {isLoading ? (
          <div className="dashboard-loading">Loading agents...</div>
        ) : filteredAgents.length === 0 ? (
          <div className="dashboard-empty">No agents found.</div>
        ) : (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Branch</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>TIN Number</th>
                  <th>Status</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {pagination.pagedItems.map((agent) => (
                  <tr key={agent.id}>
                    <td>
                      <div className="dashboard-actions">
                        {canUpdate ? (
                          <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={() => openEditForm(agent)}>Edit</button>
                        ) : null}
                        {canDelete ? (
                          <button type="button" className="profile-danger-btn profile-danger-btn-xs" onClick={() => handleDelete(agent)} disabled={isSubmitting}>Delete</button>
                        ) : null}
                        {!canUpdate && !canDelete ? <span>-</span> : null}
                      </div>
                    </td>
                    <td>{agent.full_name}</td>
                    <td>{agent.position}</td>
                    <td>{agent.branch_name || 'Unassigned'}</td>
                    <td>{agent.email || '-'}</td>
                    <td>{agent.phone || '-'}</td>
                    <td>{agent.tin_number || '-'}</td>
                    <td>
                      <span className={`status-badge status-${agent.status?.toLowerCase()}`}>{agent.status}</span>
                    </td>
                    <td>{agent.created_at || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading ? <TablePagination {...pagination} /> : null}
      </section>

      {/* ── Modal: Create / Edit Agent ── */}
      <FdModal
        isOpen={isFormOpen}
        onClose={closeForm}
        eyebrow="Agent Management"
        title={isEditMode ? 'Edit Agent' : 'Create Agent'}
        subtitle="Capture assignment, contact, and status details."
        wide
        footer={
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={closeForm} disabled={isSubmitting}>Cancel</button>
            <button
              type="submit"
              form="agent-form"
              className="fd-btn fd-btn-primary"
              disabled={isSubmitting || (isEditMode ? !canUpdate : !canCreate)}
            >
              {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Agent' : 'Create Agent')}
            </button>
          </>
        }
      >
        <form id="agent-form" onSubmit={handleSubmit}>
          <p className="fd-section-title">Agent Details</p>
          <div className="fd-modal-grid">
            <div className="fd-field fd-field-wide">
              <label htmlFor="full_name">Full Name *</label>
              <input id="full_name" type="text" name="full_name" value={formData.full_name} onChange={handleFormChange} placeholder="Enter agent name" required />
            </div>
            <div className="fd-field">
              <label htmlFor="position">Position *</label>
              <select id="position" name="position" value={formData.position} onChange={handleFormChange} required>
                {AGENT_POSITIONS.map((pos) => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>
            <div className="fd-field">
              <label htmlFor="branch_id">Branch</label>
              <select id="branch_id" name="branch_id" value={formData.branch_id} onChange={handleFormChange} disabled={isLoadingBranches || !isAdmin}>
                <option value="">Select a branch</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </div>
          </div>

          <p className="fd-section-title">Contact Information</p>
          <div className="fd-modal-grid">
            <div className="fd-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" name="email" value={formData.email} onChange={handleFormChange} placeholder="Enter email address" />
            </div>
            <div className="fd-field">
              <label htmlFor="phone">Phone</label>
              <input id="phone" type="text" name="phone" value={formData.phone} onChange={handleFormChange} placeholder="Enter phone number" />
            </div>
            <div className="fd-field">
              <label htmlFor="tin_number">TIN Number</label>
              <input id="tin_number" type="text" name="tin_number" value={formData.tin_number} onChange={handleFormChange} placeholder="Enter TIN number" />
            </div>
            <div className="fd-field">
              <label htmlFor="status">Status</label>
              <select id="status" name="status" value={formData.status} onChange={handleFormChange}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {error && <div className="error-box" style={{ marginTop: 12 }}>{error}</div>}
        </form>
      </FdModal>
    </div>
  );
}
